import classNames from './ClassEditor.module.scss';
import classes from 'classnames';
import SVG from 'react-inlinesvg';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import commonUtils from '@/utils/common';
import { ClassConfig } from '@/types/user';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import UseThemeColors from '@/hooks/UseThemeColors';
import { useAppDispatch, useAppSelector } from '@/store';
import { teacherDashboardSelector } from '@/store/reducers/teacher-dashboard';
import { GroupStudentsResponse } from '@/types/response';
import { useDrawer } from '@/context/DrawerContext';

import Detail from '../UserInfo/Detail/Detail';
import DetailSeperator from '../DetailSeperator/DetailSeperator';
import TheButton from '../TheButton/TheButton';
import AppInput from '../AppInput/AppInput';
import StudentsTable from '@/components/StudentsTable/StudentsTable';
import Switch from 'react-switch';
import SectionSeperator from '../SectionSeperator/SectionSeperator';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useNavigate } from 'react-router-dom';
import { authSelector } from '@/store/reducers/auth';

import upArrow from '@/assets/svg/up-arrow.svg';
import downArrow from '@/assets/svg/down-arrow.svg';
import pencilBlank from '@/assets/svg/pencilBlank.svg';
import pencilBlack from '@/assets/svg/pencilBlack.svg';
import copyIcon from '@/assets/svg/copy.svg';
import AppLoaderCircle from '../AppLoaderCircle';

interface ClassEditorProps {
  className?: string;
  loading?: boolean;
  classConfig: ClassConfig | null;
  showCopyModal: boolean;
  setShowCopyModal: (val: boolean) => void;
  onShowCopyModal: () => void;
  showCancelModal: boolean;
  setShowCancelModal: (val: boolean) => void;
  onShowCancelModal: () => void;
  createOrSaveClass: (classConfig: ClassConfig) => void;
  classStudentsDataResponse: GroupStudentsResponse | null;
  onShowClassLimitModal: () => void;
}

const ClassEditor = ({
  className,
  classConfig,
  loading,
  createOrSaveClass,
  showCopyModal,
  onShowCopyModal,
  setShowCopyModal,
  showCancelModal,
  setShowCancelModal,
  onShowCancelModal,
  onShowClassLimitModal,
  classStudentsDataResponse
}: ClassEditorProps) => {
  const { t } = useTranslation();
  const drawer = useDrawer();

  const navigate = useNavigate();
  const { color } = UseThemeColors();
  const { selectedClassId } = useAppSelector(teacherDashboardSelector);
  const { user } = useAppSelector(authSelector);
  const [classNameValue, setClassNameValue] = useState('');
  const [gradeValue, setGradeValue] = useState('');
  const [invitationLink, setInvitationLink] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  const schoolGrades = Array.from({ length: 12 }, (_, i) => {
    return {
      name: t(`grade_${commonUtils.fixGradeSymbol((i + 1).toString())}`),
      value: (i + 1).toString(),
    };
  });

  const namedGrades = [
    { name: t('university'), value: 'university' },
    { name: t('none'), value: 'none' },
  ];

  const grades = [...schoolGrades, ...namedGrades];

  const [shouldInitLevelTest, setShouldInitLevelTest] = useState(false);
  const [allowIndividualAdvancement, setAllowIndividualAdvancement] =
    useState(false);
  const [allowFinalAssessment, setAllowFinalAssessment] = useState(false);
  const [finalAssessmentDate, setFinalAssessmentDate] = useState<Dayjs | null>(
    null
  );
  const [canEdit, setCanEdit] = useState(false);

  const [classNameValueError, setClassNameValueError] = useState('');
  const [gradeValueError, setGradeValueError] = useState('');
  const [finalAssessmentDateError, setFinalAssessmentDateError] = useState('');

  const dateFormat = 'DD MMM YYYY';

  const handleAddStudentClick = () => {
    console.log('test', classStudentsDataResponse?.data?.data?.students && classStudentsDataResponse?.data?.data?.students.length >= 50 )
    if (classStudentsDataResponse?.data?.data?.students && classStudentsDataResponse?.data?.data?.students.length >= 50) {
        onShowClassLimitModal();
    } else {
        drawer.openAddStudent(classConfig?.id || '');
    }
};

  useEffect(() => {
    onReset();
  }, [classConfig]);

  useEffect(() => {
    window.addEventListener('click', () => {
      if (!showDropdown) {
        return;
      }

      onCloseDropdown();
    });

    return () => {
      window.removeEventListener('click', () => {});
    };
  }, [showDropdown]);

  const onReset = () => {
    setClassNameValue(classConfig?.name ?? '');
    setGradeValue(classConfig?.grade ?? '1');
    setInvitationLink(classConfig?.invitationLinkForStudents ?? '');
    setShouldInitLevelTest(classConfig?.levelTest || true);
    setAllowIndividualAdvancement(classConfig?.individualAdvancement ?? true);
    setAllowFinalAssessment(classConfig?.finalAssessment ?? true);
    setFinalAssessmentDate(
      classConfig?.finalAssessmentDate
        ? dayjs(classConfig?.finalAssessmentDate)
        : dayjs()
    );
    setCanEdit(!classConfig?.id);

    setClassNameValueError('');
    setGradeValueError('');
    setFinalAssessmentDateError('');
  };

  const onPickDate = (newValue: Dayjs | null) => {
    setFinalAssessmentDate(newValue);
    setFinalAssessmentDateError('');
  };

  const onToggleDropdown = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setShowDropdown((prevState) => !prevState);
  };

  const onCloseDropdown = () => {
    setShowDropdown(false);
  };

  const onSelectedOption = (
    e: React.MouseEvent<HTMLElement>,
    option: string
  ) => {
    console.log(option);
    e.stopPropagation();
    onCloseDropdown();
    setGradeValue(option);
    setGradeValueError('');
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'auto',
    });
  };

  const onCopy = () => {
    onShowCopyModal();
    navigator.clipboard.writeText(invitationLink);
  };

  const onCancel = () => {
    if (user?.teacherGroups?.length === 0) {
      onShowCancelModal();
    } else {
      scrollToTop();
      navigate('/dashboard/my-students');
    }
  };

  const onNext = () => {
    // scrollToTop();

    const differenceInDays = finalAssessmentDate?.diff(dayjs(), 'day') ?? 0;

    const isClassNameValid = classNameValue.trim();
    const isGradeValid = gradeValue.trim();
    const isDateValid = differenceInDays >= 0;

    let isValid = isClassNameValid && isGradeValid && isDateValid;

    if (!isClassNameValid) {
      setClassNameValueError(t('invalidClassName') ?? ' ');
    }

    if (!isGradeValid) {
      setGradeValueError(t('invalidGrade') ?? ' ');
    }

    if (!isDateValid && allowFinalAssessment) {
      setFinalAssessmentDateError(t('invalidDate') ?? ' ');
    }

    if (!isValid) {
      return;
    }

    // dispatch(teacherDashboardActions.onReset());
    // onReset();
    createOrSaveClass({
      name: classNameValue,
      grade: gradeValue,
      levelTest: shouldInitLevelTest,
      individualAdvancement: allowIndividualAdvancement,
      finalAssessment: allowFinalAssessment,
      finalAssessmentDate: dayjs(finalAssessmentDate),
    });
  };

  const studentsAmount = 0;
  // const studentsAmount = 42;

  const gradeOptions = grades.map((grade) => {
    const isSelected = grade.value === gradeValue;

    return (
      <div
        key={grade.name}
        className={classNames.grade}
        onClick={(event) => onSelectedOption(event, grade.value)}
      >
        {grade.name}
      </div>
    );
  });

  const gradesDropdown = showDropdown && (
    <div className={classNames.gradesDropdown}>{gradeOptions}</div>
  );

  const isUniversityGrade = gradeValue === 'university';
  const isNoneGrade = gradeValue === 'none';

  const gradesSelect = (
    <div
      className={classes(classNames.gradesSelectContainer, {
        [classNames.gradesSelectContainerError]: gradeValueError,
        [classNames.fieldDisabled]: !canEdit,
      })}
    >
      <span className={classNames.label}>{t('grade')}</span>
      <div
        className={classes(classNames.gradesSelect, {
          [classNames.fieldDisabled]: !canEdit,
        })}
        onClick={onToggleDropdown}
      >
        <span className={!gradeValue ? 'opacity-40' : ''}>
          {gradeValue
            ? isUniversityGrade || isNoneGrade
              ? t(gradeValue)
              : t(`grade_${gradeValue}`)
            : t('choose')}
        </span>
        <SVG src={showDropdown ? upArrow : downArrow} />
      </div>
      {gradesDropdown}
    </div>
  );

  return (
    <div className={classes(classNames.classEditor, className)}>
      <div className={classes(classNames.classEditorInner)}>
        <div className={classNames.header}>
          <div className={classNames.texts}>
            <h2>{classNameValue || t('className')}</h2>
            <div className={classNames.details}>
              {gradeValue && (
                <Detail
                  className={classNames.detail}
                  topic={t('grade')}
                  value={
                    isUniversityGrade || isNoneGrade
                      ? t(gradeValue)
                      : t(`grade_${gradeValue}`)
                  }
                />
              )}

              {studentsAmount > 0 && (
                <>
                  <DetailSeperator />
                  <Detail
                    className={classNames.detail}
                    topic={t('students')}
                    value={studentsAmount}
                  />
                </>
              )}
            </div>
          </div>
          {classConfig?.id && (
            <button
              onClick={() => {
                setCanEdit((prev) => !prev);
                scrollToTop();
              }}
              className={classNames.editIcon}
            >
              <SVG src={canEdit ? pencilBlack : pencilBlank} />
            </button>
          )}
        </div>
        <SectionSeperator className="mt-6 mb-8" />
        <div className={classNames.form}>
          <div
            className={classes(classNames.fieldsGroup, 'flex flex-col gap-y-6')}
          >
            <div className={classNames.nameAndGradeFields}>
              <AppInput
                className={classes(classNames.field, {
                  [classNames.classNameFieldError]: classNameValueError,
                  [classNames.fieldDisabled]: !canEdit,
                })}
                id="class_name"
                name="className"
                label={t('className') ?? ''}
                placeholder={t('writeHere') ?? ''}
                value={classNameValue}
                emitChange={({ target }) => {
                  setClassNameValue(target.value);
                  setClassNameValueError('');
                }}
              />
              {gradesSelect}
            </div>
            {selectedClassId && invitationLink && (
              <>
                {/* <StudentsPicker /> */}
                <div className={classNames.invitationLinkField}>
                  <span className={classNames.label}>
                    {t('invitationLink')}
                  </span>
                  <button onClick={onCopy}>
                    <span>{invitationLink}</span>
                    <SVG src={copyIcon} />
                  </button>
                </div>
              </>
            )}
          </div>
          {classConfig?.id && classConfig?.id !== undefined && !invitationLink && classStudentsDataResponse?.data?.data?.students && (
            <>
            <StudentsTable
              studentsData={classStudentsDataResponse?.data?.data?.students}
              allowActions={false}
              allowNewRows={false}
              title={t('students')}
            />
            {/* <button onClick={() => drawer.openAddStudent(classConfig?.id || '')}>{t('settings.add_students.add_student')}</button> */}
            <button onClick={handleAddStudentClick}>{t('settings.add_students.add_student')}</button>
            </>
            
          )}
          {!classConfig?.id && (
            <div
              className={classes(classNames.fieldsGroup, {
                [classNames.fieldsGroupDisabled]: !canEdit,
              })}
            >
              <div className="flex items-center justify-between">
                <label className={classNames.label} htmlFor="init_level_test">
                  {t('initialLevelSetTest')}
                </label>
                <Switch
                  id="init_level_test"
                  offColor={color('grey-light9')}
                  onColor={color('green-regular5')}
                  activeBoxShadow=""
                  height={32}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  onChange={(val) => setShouldInitLevelTest(val)}
                  checked={shouldInitLevelTest}
                />
              </div>
              <p className={classNames.description}>
                {shouldInitLevelTest
                  ? t('uponRegistrationStudentsWillReceiveAnInitialLevelTest')
                  : t('yourStudentsInThisClassWillNotTakeTheInitialLevelTest')}
              </p>
            </div>
          )}
          <div
            className={classes(classNames.fieldsGroup, {
              [classNames.fieldsGroupDisabled]: !canEdit,
            })}
          >
            <div className={classNames.englishSkillsAssessmentsField}>
              <span className={classNames.label}>
                {t('englishSkillsAssessments')}
              </span>
              <p className={classNames.description}>
                {t('whenStudentsWillHaveCompletedAParticularSkill')}
              </p>
            </div>
            <SectionSeperator className="my-6" />
            <div className={classNames.allowIndividualAdvancementField}>
              <div className="flex items-center justify-between">
                <label
                  className={classNames.label}
                  htmlFor="allow_individual_advancement"
                >
                  {t('allowIndividualAdvancement')}
                </label>
                <Switch
                  id="allow_individual_advancement"
                  offColor={color('grey-light9')}
                  onColor={color('green-regular5')}
                  activeBoxShadow=""
                  height={32}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  onChange={(val) => setAllowIndividualAdvancement(val)}
                  checked={allowIndividualAdvancement}
                />
              </div>
              <p className={classNames.description}>
                {allowIndividualAdvancement
                  ? t('whenYourStudentsReceiveAPassingGrade')
                  : t('studentsAreNotPermittedToAdvanceToTheNextLevel')}
              </p>
            </div>
          </div>
          <div
            className={classes(classNames.fieldsGroup, {
              [classNames.fieldsGroupDisabled]: !canEdit,
            })}
          >
            <div className={classNames.allowFinalAssessmentField}>
              <div className="flex items-center justify-between">
                <label
                  className={classNames.label}
                  htmlFor="allow_final_assessment"
                >
                  {t('endOfLevelAssessment')}
                </label>
                <Switch
                  id="allow_final_assessment"
                  offColor={color('grey-light9')}
                  onColor={color('green-regular5')}
                  activeBoxShadow=""
                  height={32}
                  uncheckedIcon={false}
                  checkedIcon={false}
                  onChange={(val) => setAllowFinalAssessment(val)}
                  checked={allowFinalAssessment}
                />
              </div>
              <p className={classNames.description}>
                {allowFinalAssessment
                  ? t(
                      'thisTestIsDesignedToAssessYourStudentsInTheirGeneralEnglishLevel'
                    )
                  : t('yourStudentsWillNotReceiveAFinalExam')}
              </p>
            </div>
            {allowFinalAssessment && (
              <>
                <SectionSeperator className="my-6" />
                <div
                  className={classes(classNames.finalAssessmentDate, {
                    [classNames.finalAssessmentDateError]:
                      finalAssessmentDateError,
                  })}
                >
                  <span className={classNames.label}>{t('chooseDate')}</span>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      className={classNames.datePicker}
                      format={dateFormat}
                      minDate={dayjs()}
                      defaultValue={finalAssessmentDate}
                      value={finalAssessmentDate}
                      onChange={onPickDate}
                    />
                  </LocalizationProvider>
                </div>
              </>
            )}
          </div>
          <div className={classNames.bottom}>
            {/* {classConfig?.id && (
            <button className={classNames.deleteBtn}>
              <SVG src={bin} />
              {t('deleteClass')}
            </button>
          )} */}
            <div className={classNames.buttons}>
              <TheButton
                className={classNames.cancelBtn}
                text={t('cancel')}
                plain
                emitOnClick={onCancel}
              />
              {loading ? (
                <div className={classNames.loading}>
                  <AppLoaderCircle />
                </div>
              ) : (
                <TheButton
                  className={classNames.nextBtn}
                  text={classConfig?.id ? t('save') : t('create')}
                  shouldRecolor={false}
                  disabled={!canEdit}
                  emitOnClick={onNext}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassEditor;
